"use client";

import nowadaysLogo from "@/app/(public)/shared/assets/nowadayslogo.png";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/spacing.css";
import "@/app/(public)/shared/tokens/typography.css";
import Image from "next/image";
import Link from "next/link";
import { FooterWordmark } from "./FooterWordmark";

export const Footer = () => {
  return (
    <footer className="group/footer w-full flex flex-col items-center border-t bg-landing-primary">
      <div className="container-page py-16 relative flex flex-col gap-32 border-landing-primary">
        {/* Main Footer Content */}
        <div className="flex flex-col gap-16">
          {/* Company Info + Link Categories Container */}
          <div className="flex flex-col md:flex-row gap-16 md:gap-24">
            {/* Company Info */}
            <div className="flex flex-col gap-4 max-w-[300px] flex-shrink-0">
              <div className="flex items-center gap-3">
                <Image src={nowadaysLogo.src} width={140} height={48} alt="Nowadays Logo" />
              </div>
              <div className="text-body-sm text-landing-secondary opacity-70">
                Enterprise-grade event management platform. Streamline your corporate events with AI-powered planning,
                from venue sourcing to budget management.
              </div>
              <a
                href="mailto:events@nowadays.ai"
                className="text-tag-sm text-landing-secondary opacity-60 hover:opacity-100 transition-opacity duration-200"
              >
                events@nowadays.ai
              </a>
              {/* Certifications */}
              <div className="mt-2 flex items-center gap-4">
                <Link href="https://www.iata.org/" target="_blank" rel="noopener noreferrer">
                  <div className="hover:opacity-80 transition-opacity group">
                    <Image
                      src="/landing/iata.svg"
                      width={120}
                      height={40}
                      alt="IATA Accredited Agent"
                      className="opacity-70 group-hover:opacity-100 transition-opacity"
                    />
                  </div>
                </Link>
                <Link href="https://www.wbenc.org/" target="_blank" rel="noopener noreferrer">
                  <div className="hover:opacity-80 transition-opacity group">
                    <Image
                      src="/landing/wbenc-certified.webp"
                      width={70}
                      height={40}
                      alt="WBENC Certified Women's Business Enterprise"
                      className="opacity-70 group-hover:opacity-100 transition-opacity"
                    />
                  </div>
                </Link>
              </div>
            </div>

            {/* Footer Categories - 2x2 Grid on Mobile, 4 Columns on Desktop */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16 flex-1">
              {/* Company */}
              <div className="flex flex-col gap-6">
                <div className="text-tag-md font-semibold text-landing-primary">Company</div>
                <div className="flex flex-col gap-4">
                  <Link
                    href="/company"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    About Nowadays
                  </Link>
                  <Link
                    href="/press"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Newsroom
                  </Link>
                  <Link
                    href="/team"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Our Team
                  </Link>
                  <Link
                    href="/careers"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Careers
                  </Link>
                  <Link
                    href="/global-account-managers"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Planner Profiles
                  </Link>
                  <Link
                    href="/pricing"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Pricing
                  </Link>
                  <Link
                    href="/partner"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Partner with us
                  </Link>
                  <Link
                    href="/referral"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Referral Program
                  </Link>
                </div>
              </div>

              {/* Solutions */}
              <div className="flex flex-col gap-6">
                <div className="text-tag-md font-semibold text-landing-primary">Solutions</div>
                <div className="flex flex-col gap-4">
                  <Link
                    href="/solutions/startups"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Startups
                  </Link>
                  <Link
                    href="/solutions/enterprise"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Enterprise
                  </Link>
                  <Link
                    href="/people-teams"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    People Teams
                  </Link>
                  <Link
                    href="/executive-assistants"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Executive Assistants
                  </Link>
                  <Link
                    href="/event-marketers"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Event Marketers
                  </Link>
                  <Link
                    href="/agencies"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Agencies
                  </Link>
                  <Link
                    href="/travel-agents"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Travel Agents
                  </Link>
                  <Link
                    href="/partners"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Hoteliers & Venues
                  </Link>
                </div>
              </div>

              {/* Products */}
              <div className="flex flex-col gap-6">
                <div className="text-tag-md font-semibold text-landing-primary">Products</div>
                <div className="flex flex-col gap-4">
                  <Link
                    href="/venue-sourcing"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Venue Sourcing
                  </Link>
                  <Link
                    href="/registration"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Registration
                  </Link>
                  <Link
                    href="/flight-estimator"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Flight Estimator
                  </Link>
                  <Link
                    href="/budget-estimator"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Budget Estimator
                  </Link>
                  <Link
                    href="/venue-collective"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    The Venue Collective
                  </Link>
                  <Link
                    href="/create"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Create Event
                  </Link>
                  <Link
                    href="/reporting"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Reporting & Analytics
                  </Link>
                  <Link
                    href="/site-pages"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Custom Event Sites
                  </Link>

                  <Link
                    href="/hotel-room-blocks"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Hotel Room Blocks
                  </Link>
                  <Link
                    href="/activations"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Marketing Activations
                  </Link>
                  <Link
                    href="/games"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Games
                  </Link>
                </div>
              </div>

              {/* Resources */}
              <div className="flex flex-col gap-6">
                <div className="text-tag-md font-semibold text-landing-primary">Resources</div>
                <div className="flex flex-col gap-4">
                  <Link
                    href="/case-studies"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Case Studies
                  </Link>
                  <Link
                    href="/blogs"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Blog
                  </Link>
                  <Link
                    href="/comparisons"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Comparisons
                  </Link>
                  <Link
                    href="/templates"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Templates
                  </Link>
                  <Link
                    href="/support"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Support
                  </Link>
                  <Link
                    href="https://status.nowadays.ai/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Status
                  </Link>
                  <Link
                    href="/community"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Community
                  </Link>
                  <Link
                    href="/changelog"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Change Log
                  </Link>
                  <Link
                    href="/newsletter"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Newsletter
                  </Link>
                  <Link
                    href="/faq"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    FAQs
                  </Link>
                  <Link
                    href="/glossary"
                    className="text-tag-sm text-landing-secondary opacity-70 hover:opacity-100 transition-opacity duration-200"
                  >
                    Glossary
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 border-landing-primary">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="text-tag-sm text-landing-secondary opacity-60">
                © {new Date().getFullYear()} Nowadays AI Corp.
              </div>

              <div className="hidden md:block w-1 h-1 rounded-full bg-border-landing-primary opacity-30"></div>
              <div className="flex items-center gap-6">
                <Link
                  href="/terms-of-service"
                  className="text-tag-sm text-landing-secondary opacity-60 hover:opacity-100 transition-opacity duration-200"
                >
                  Terms of Service
                </Link>
                <Link
                  href="/privacy-policy"
                  className="text-tag-sm text-landing-secondary opacity-60 hover:opacity-100 transition-opacity duration-200"
                >
                  Privacy Policy
                </Link>
                <Link
                  href="/newsletter"
                  className="text-tag-sm text-landing-secondary opacity-60 hover:opacity-100 transition-opacity duration-200"
                >
                  Newsletter
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <a href="https://elevenlabs.io/text-to-speech">
                <Image
                  src="https://eleven-public-cdn.elevenlabs.io/payloadcms/pwsc4vchsqt-ElevenLabsGrants.webp"
                  alt="Text to Speech"
                  width={200}
                  height={200}
                />
              </a>
              {/* TechCrunch Link */}
              <Link
                href="https://techcrunch.com/2024/12/06/y-combinator-alum-nowadays-founded-by-sisters-raises-2m-to-automate-event-planning/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="hover:opacity-80 transition-opacity">
                  <Image src="/landing/techcrunch_button.svg" width={28} height={28} alt="TechCrunch Feature" />
                </div>
              </Link>
              {/* LinkedIn Link */}
              <Link href="https://www.linkedin.com/company/getnowadays/" target="_blank" rel="noopener noreferrer">
                <div className="hover:opacity-80 transition-opacity">
                  <Image src="/landing/linked_in_button.svg" width={28} height={28} alt="LinkedIn" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <FooterWordmark />
    </footer>
  );
};
